import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import * as fabric from 'fabric';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Save, RotateCcw, Eye, Loader2, Type, Square, Minus, Grid3x3,
  Trash2, Copy, AlignLeft, AlignCenter, AlignRight, ChevronDown,
  Plus, FileText, MousePointer2, Download, Upload, Star, FolderOpen,
  FilePlus, Pencil, Check, X
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// A4 at 72 DPI: 595 x 842 pts. We scale to fit screen.
const PDF_W = 595, PDF_H = 842;
const CANVAS_SCALE = 0.72; // screen scale
const CW = Math.round(PDF_W * CANVAS_SCALE);
const CH = Math.round(PDF_H * CANVAS_SCALE);
const MARGIN = 42; // 15mm in pts

// デフォルトの請求書要素
function defaultInvoiceElements() {
  return [
    { id: 'svc_label', type: 'textbox', text: '(日中一時支援)', left: 50, top: 30, fontSize: 10, width: 200, fontFamily: 'sans-serif', textAlign: 'left' },
    { id: 'title', type: 'textbox', text: '請　求　書', left: 180, top: 55, fontSize: 26, width: 250, fontFamily: 'sans-serif', textAlign: 'center', fontWeight: 'bold' },
    { id: 'recipient', type: 'textbox', text: '(請求先)　津　市　長　様', left: 50, top: 110, fontSize: 13, width: 300, fontFamily: 'sans-serif' },
    { id: 'amt_label', type: 'rect', left: 50, top: 155, width: 100, height: 40, label: '請求金額' },
    { id: 'amt_digits', type: 'rect', left: 150, top: 155, width: 370, height: 40, label: '{{請求合計}} 円' },
    { id: 'naiyaku_label', type: 'rect', left: 50, top: 220, width: 30, height: 95, label: '内\n訳' },
    { id: 'naiyaku_header', type: 'rect', left: 80, top: 220, width: 440, height: 24, label: '令和{{令和年}}年 {{月}}月利用分' },
    { id: 'naiyaku_cols', type: 'rect', left: 80, top: 244, width: 440, height: 24, label: '請求費名 | 明細書件数 | 金額' },
    { id: 'naiyaku_data', type: 'rect', left: 80, top: 268, width: 440, height: 24, label: '日中一時支援 | {{件数}}件 | {{請求合計}}円' },
    { id: 'naiyaku_total', type: 'rect', left: 80, top: 292, width: 440, height: 23, label: '合計 | {{件数}}件 | {{請求合計}}円' },
    { id: 'statement', type: 'textbox', text: '上記のとおり請求します。', left: 50, top: 340, fontSize: 12, width: 300, fontFamily: 'sans-serif' },
    { id: 'date_line', type: 'textbox', text: '令和　{{翌令和年}}　年　　{{翌月}}　月　　{{日}}　日', left: 200, top: 370, fontSize: 12, width: 300, fontFamily: 'sans-serif', textAlign: 'center' },
    { id: 'biz_label', type: 'rect', left: 50, top: 410, width: 60, height: 120, label: '請求\n事業者' },
    { id: 'biz_name', type: 'rect', left: 110, top: 410, width: 60, height: 30, label: '事業所名' },
    { id: 'biz_name_val', type: 'rect', left: 170, top: 410, width: 350, height: 30, label: '{{会社名}}\n{{事業所名}}' },
    { id: 'biz_addr', type: 'rect', left: 110, top: 440, width: 60, height: 30, label: '住所(所在地)' },
    { id: 'biz_addr_val', type: 'rect', left: 170, top: 440, width: 350, height: 30, label: '〒{{郵便番号}}\n{{住所}}' },
    { id: 'biz_phone', type: 'rect', left: 110, top: 470, width: 60, height: 30, label: '電話番号' },
    { id: 'biz_phone_val', type: 'rect', left: 170, top: 470, width: 350, height: 30, label: '{{電話番号}}' },
    { id: 'biz_rep', type: 'rect', left: 110, top: 500, width: 60, height: 30, label: '職・氏名' },
    { id: 'biz_rep_val', type: 'rect', left: 170, top: 500, width: 350, height: 30, label: '{{代表者}}　　（印）' },
    { id: 'bank_title', type: 'textbox', text: '【振込先】', left: 50, top: 555, fontSize: 12, width: 150, fontFamily: 'sans-serif' },
    { id: 'bank_1', type: 'textbox', text: '金　融　機　関　名　　{{銀行名}}　{{支店名}}', left: 70, top: 580, fontSize: 11, width: 450, fontFamily: 'sans-serif' },
    { id: 'bank_2', type: 'textbox', text: '預　金　種　別　　{{口座種別}}口座', left: 70, top: 600, fontSize: 11, width: 450, fontFamily: 'sans-serif' },
    { id: 'bank_3', type: 'textbox', text: '口　座　番　号　　{{口座番号}}', left: 70, top: 620, fontSize: 11, width: 450, fontFamily: 'sans-serif' },
    { id: 'bank_4', type: 'textbox', text: '名　義　人　　{{口座名義}}', left: 70, top: 640, fontSize: 11, width: 450, fontFamily: 'sans-serif' },
  ];
}

function defaultDetailElements() {
  return [
    { id: 'svc_label', type: 'textbox', text: '(日中一時支援)', left: 50, top: 30, fontSize: 9, width: 200 },
    { id: 'title', type: 'textbox', text: '津市日中一時支援事業利用明細書', left: 120, top: 60, fontSize: 16, width: 380, textAlign: 'center', fontWeight: 'bold' },
    { id: 'date', type: 'textbox', text: '令和　X　年　　Y　月利用分', left: 320, top: 95, fontSize: 11, width: 220, textAlign: 'right' },
    { id: 'hdr_lbl1', type: 'rect', left: 50, top: 125, width: 80, height: 24, label: '事業所名' },
    { id: 'hdr_val1', type: 'rect', left: 130, top: 125, width: 180, height: 24, label: '（事業所名）' },
    { id: 'hdr_lbl2', type: 'rect', left: 50, top: 149, width: 80, height: 24, label: '障がい者氏名' },
    { id: 'hdr_val2', type: 'rect', left: 130, top: 149, width: 180, height: 24, label: '（氏名）' },
    { id: 'cost_label', type: 'rect', left: 50, top: 200, width: 50, height: 250, label: '費用額\n計算欄' },
    { id: 'cost_table', type: 'rect', left: 100, top: 200, width: 420, height: 250, label: 'サービス内容 | 単位数 | 算定回数 | 利用単位数 | 摘要' },
    { id: 'burden_label', type: 'rect', left: 50, top: 475, width: 50, height: 100, label: '利用者\n負担額\n計算欄' },
    { id: 'burden_table', type: 'rect', left: 100, top: 475, width: 420, height: 100, label: '利用者負担額等の内訳 | 当月算定額 | 摘要' },
    { id: 'total_box', type: 'rect', left: 50, top: 600, width: 250, height: 30, label: '当月利用請求額②-③ | 円' },
  ];
}

function defaultRecordElements() {
  return [
    { id: 'svc_label', type: 'textbox', text: '(日中一時支援)', left: 50, top: 30, fontSize: 9, width: 200 },
    { id: 'title', type: 'textbox', text: '令和X年Y月利用分　津市日中一時支援事業利用サービス提供実績記録票', left: 50, top: 55, fontSize: 11, width: 500, textAlign: 'center' },
    { id: 'hdr_table', type: 'rect', left: 50, top: 85, width: 500, height: 60, label: '受給者証番号 | 支給量等 | 事業所名 | 障がい者氏名' },
    { id: 'grid', type: 'rect', left: 50, top: 155, width: 500, height: 580, label: '日付 | 曜日 | 計画開始 | 計画終了 | 提供開始 | 提供終了 | 算定時間 | 食事 | ...' },
  ];
}

function createFabricObject(el) {
  const scale = CANVAS_SCALE;
  if (el.type === 'textbox') {
    return new fabric.Textbox(el.text || '', {
      left: el.left * scale,
      top: el.top * scale,
      width: (el.width || 200) * scale,
      fontSize: (el.fontSize || 12) * scale,
      fontFamily: 'sans-serif',
      fontWeight: el.fontWeight || 'normal',
      textAlign: el.textAlign || 'left',
      fill: '#000',
      borderColor: '#3b82f6',
      cornerColor: '#3b82f6',
      cornerStyle: 'circle',
      cornerSize: 8,
      transparentCorners: false,
      padding: 4,
      _elId: el.id,
      _elType: 'textbox',
    });
  }
  if (el.type === 'rect') {
    const w = (el.width || 100) * scale;
    const h = (el.height || 30) * scale;
    const rect = new fabric.Rect({
      left: el.left * scale,
      top: el.top * scale,
      width: w,
      height: h,
      fill: 'rgba(248,250,252,0.95)',
      stroke: '#000',
      strokeWidth: 0.8,
      borderColor: '#3b82f6',
      cornerColor: '#3b82f6',
      cornerStyle: 'circle',
      cornerSize: 8,
      transparentCorners: false,
      _elId: el.id,
      _elType: 'rect',
      _label: el.label || '',
    });
    // ラベルテキストをカスタム描画
    rect.on('after:render', function(opt) {
      if (!this.canvas) return;
      const ctx = this.canvas.getContext('2d');
      ctx.save();
      ctx.font = `${6 * scale}px sans-serif`;
      ctx.fillStyle = '#555';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const center = this.getCenterPoint();
      const lines = (this._label || '').split('\n');
      lines.forEach((line, i) => {
        ctx.fillText(line, center.x, center.y + (i - (lines.length - 1) / 2) * 8 * scale);
      });
      ctx.restore();
    });
    return rect;
  }
  if (el.type === 'line') {
    return new fabric.Line([
      (el.x1 || 0) * scale, (el.y1 || 0) * scale,
      (el.x2 || 100) * scale, (el.y2 || 0) * scale,
    ], {
      stroke: '#000',
      strokeWidth: 1,
      _elId: el.id,
      _elType: 'line',
      borderColor: '#3b82f6',
      cornerColor: '#3b82f6',
    });
  }
  return null;
}

export default function PDFDesignPage() {
  const canvasRef = useRef(null);
  const fabricRef = useRef(null);
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [activeTab, setActiveTab] = useState('invoice');
  const [drawMode, setDrawMode] = useState(null);
  const drawStart = useRef(null);

  // テンプレート管理
  const [templates, setTemplates] = useState([]);
  const [currentTemplateId, setCurrentTemplateId] = useState(null);
  const [currentTemplateName, setCurrentTemplateName] = useState('');
  const [showSaveAs, setShowSaveAs] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  // キャンバス初期化
  useEffect(() => {
    if (!canvasRef.current) return;
    const fc = new fabric.Canvas(canvasRef.current, {
      width: CW, height: CH,
      backgroundColor: '#fff',
      selection: true,
    });
    fabricRef.current = fc;

    fc.on('selection:created', (e) => updateSelected(e.selected?.[0]));
    fc.on('selection:updated', (e) => updateSelected(e.selected?.[0]));
    fc.on('selection:cleared', () => setSelected(null));
    fc.on('object:modified', () => updateSelected(fc.getActiveObject()));

    loadCanvasData(fc, 'invoice');
    setLoading(false);

    return () => { fc.dispose(); };
  }, []);

  // タブ切替時にキャンバスを再読込
  useEffect(() => {
    if (!fabricRef.current) return;
    loadCanvasData(fabricRef.current, activeTab);
  }, [activeTab]);

  // テンプレート一覧読込
  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/pdf-templates-list`);
      setTemplates(res.data || []);
    } catch {}
  };
  useEffect(() => { fetchTemplates(); }, []);

  // テンプレート保存（上書き）
  const handleSaveTemplate = async () => {
    setSaving(true);
    try {
      const elements = getCanvasElements();
      if (currentTemplateId) {
        await axios.put(`${API}/pdf-templates-list/${currentTemplateId}`, {
          canvas: { [activeTab]: elements },
        });
        toast.success('テンプレートを上書き保存しました');
      } else {
        setShowSaveAs(true);
        setSaveAsName('新規テンプレート');
      }
      await fetchTemplates();
    } catch { toast.error('保存失敗'); }
    finally { setSaving(false); }
  };

  // 名前をつけて保存
  const handleSaveAs = async () => {
    if (!saveAsName.trim()) return;
    try {
      const elements = getCanvasElements();
      const res = await axios.post(`${API}/pdf-templates-list`, {
        name: saveAsName.trim(),
        doc_type: activeTab,
        canvas: { [activeTab]: elements },
      });
      setCurrentTemplateId(res.data.id);
      setCurrentTemplateName(saveAsName.trim());
      setShowSaveAs(false);
      toast.success(`「${saveAsName.trim()}」を保存しました`);
      await fetchTemplates();
    } catch { toast.error('保存失敗'); }
  };

  // テンプレート読込
  const handleLoadTemplate = async (id) => {
    try {
      const res = await axios.get(`${API}/pdf-templates-list/${id}`);
      const data = res.data;
      setCurrentTemplateId(id);
      setCurrentTemplateName(data.name || '');
      if (data.canvas && fabricRef.current) {
        const tab = data.doc_type !== 'all' ? data.doc_type : activeTab;
        setActiveTab(tab);
        const fc = fabricRef.current;
        fc.clear();
        fc.backgroundColor = '#fff';
        const s = CANVAS_SCALE;
        const border = new fabric.Rect({
          left: MARGIN * s - 2, top: MARGIN * s - 2,
          width: (PDF_W - MARGIN * 2) * s + 4, height: (PDF_H - MARGIN * 2) * s + 4,
          fill: 'transparent', stroke: '#ccc', strokeWidth: 0.5,
          selectable: false, evented: false,
        });
        fc.add(border);
        const els = data.canvas[tab] || data.canvas[Object.keys(data.canvas)[0]] || [];
        els.forEach(el => { const obj = createFabricObject(el); if (obj) fc.add(obj); });
        fc.renderAll();
      }
      toast.success(`「${data.name}」を読み込みました`);
    } catch { toast.error('読込失敗'); }
  };

  // テンプレート削除
  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('このテンプレートを削除しますか？')) return;
    try {
      await axios.delete(`${API}/pdf-templates-list/${id}`);
      if (currentTemplateId === id) { setCurrentTemplateId(null); setCurrentTemplateName(''); }
      toast.success('削除しました');
      await fetchTemplates();
    } catch { toast.error('削除失敗'); }
  };

  // デフォルト設定
  const handleSetDefault = async (id) => {
    try {
      await axios.post(`${API}/pdf-templates-list/${id}/set-default`);
      toast.success('デフォルトに設定しました');
      await fetchTemplates();
    } catch { toast.error('設定失敗'); }
  };

  // リネーム
  const handleRename = async (id) => {
    if (!renameValue.trim()) return;
    try {
      await axios.put(`${API}/pdf-templates-list/${id}`, { name: renameValue.trim() });
      if (currentTemplateId === id) setCurrentTemplateName(renameValue.trim());
      setRenamingId(null);
      await fetchTemplates();
    } catch { toast.error('名前変更失敗'); }
  };

  // エクスポート
  const handleExport = async (id) => {
    try {
      const res = await axios.get(`${API}/pdf-templates-list/${id}/export`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${res.data.name || 'template'}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('エクスポート失敗'); }
  };

  // インポート
  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        await axios.post(`${API}/pdf-templates-list/import`, data);
        toast.success('インポートしました');
        await fetchTemplates();
      } catch { toast.error('インポート失敗'); }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const updateSelected = (obj) => {
    if (!obj) { setSelected(null); return; }
    const s = CANVAS_SCALE;
    setSelected({
      id: obj._elId || '',
      type: obj._elType || obj.type,
      left: Math.round(obj.left / s),
      top: Math.round(obj.top / s),
      width: Math.round((obj.width * (obj.scaleX || 1)) / s),
      height: Math.round((obj.height * (obj.scaleY || 1)) / s),
      text: obj.text || '',
      fontSize: obj.fontSize ? Math.round(obj.fontSize / s) : undefined,
      fontWeight: obj.fontWeight,
      textAlign: obj.textAlign,
      label: obj._label || '',
    });
  };

  async function loadCanvasData(fc, tab) {
    fc.clear();
    fc.backgroundColor = '#fff';
    // ページ枠線
    const border = new fabric.Rect({
      left: MARGIN * CANVAS_SCALE - 2,
      top: MARGIN * CANVAS_SCALE - 2,
      width: (PDF_W - MARGIN * 2) * CANVAS_SCALE + 4,
      height: (PDF_H - MARGIN * 2) * CANVAS_SCALE + 4,
      fill: 'transparent', stroke: '#ccc', strokeWidth: 0.5,
      selectable: false, evented: false,
    });
    fc.add(border);

    try {
      const res = await axios.get(`${API}/pdf-design`);
      const canvasData = res.data?._canvas?.[tab];
      if (canvasData && canvasData.length > 0) {
        canvasData.forEach(el => {
          const obj = createFabricObject(el);
          if (obj) fc.add(obj);
        });
        fc.renderAll();
        return;
      }
    } catch {}

    // デフォルト要素
    const defaults = tab === 'invoice' ? defaultInvoiceElements()
      : tab === 'detail' ? defaultDetailElements()
      : defaultRecordElements();
    defaults.forEach(el => {
      const obj = createFabricObject(el);
      if (obj) fc.add(obj);
    });
    fc.renderAll();
  }

  function getCanvasElements() {
    if (!fabricRef.current) return [];
    const s = CANVAS_SCALE;
    return fabricRef.current.getObjects().filter(o => o.selectable !== false).map(obj => {
      const base = {
        id: obj._elId || `el_${Date.now()}`,
        left: Math.round(obj.left / s),
        top: Math.round(obj.top / s),
        width: Math.round((obj.width * (obj.scaleX || 1)) / s),
        height: Math.round((obj.height * (obj.scaleY || 1)) / s),
      };
      if (obj._elType === 'textbox' || obj.type === 'textbox') {
        return { ...base, type: 'textbox', text: obj.text, fontSize: Math.round((obj.fontSize || 12) / s), fontWeight: obj.fontWeight, textAlign: obj.textAlign };
      }
      if (obj._elType === 'rect' || (obj.type === 'rect' && obj._elId)) {
        return { ...base, type: 'rect', label: obj._label || '' };
      }
      if (obj._elType === 'line' || obj.type === 'line') {
        return { ...base, type: 'line', x1: base.left, y1: base.top, x2: base.left + base.width, y2: base.top + base.height };
      }
      return { ...base, type: 'textbox', text: obj.text || '' };
    });
  }

  const handleSave = async () => {
    setSaving(true);
    try {
      const currentElements = getCanvasElements();
      const res = await axios.get(`${API}/pdf-design`);
      const existing = res.data || {};
      const canvasState = existing._canvas || {};
      canvasState[activeTab] = currentElements;
      await axios.put(`${API}/pdf-design`, { ...existing, _canvas: canvasState });
      toast.success('デザインを保存しました');
    } catch { toast.error('保存失敗'); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm('デフォルトにリセットしますか？')) return;
    try {
      await axios.post(`${API}/pdf-design/reset`);
      if (fabricRef.current) loadCanvasData(fabricRef.current, activeTab);
      toast.success('リセットしました');
    } catch { toast.error('リセット失敗'); }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      await handleSave();
      const res = await axios.get(`${API}/pdf-design/preview?billing_month=2024-01`, { responseType: 'blob' });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })));
    } catch { toast.error('プレビュー失敗'); }
    finally { setPreviewing(false); }
  };

  // ツールバーアクション
  const addText = () => {
    if (!fabricRef.current) return;
    const s = CANVAS_SCALE;
    const obj = new fabric.Textbox('テキスト', {
      left: 100 * s, top: 100 * s, width: 150 * s,
      fontSize: 12 * s, fontFamily: 'sans-serif', fill: '#000',
      borderColor: '#3b82f6', cornerColor: '#3b82f6', cornerStyle: 'circle',
      cornerSize: 8, transparentCorners: false,
      _elId: `text_${Date.now()}`, _elType: 'textbox',
    });
    fabricRef.current.add(obj);
    fabricRef.current.setActiveObject(obj);
    fabricRef.current.renderAll();
  };

  const addRect = () => {
    if (!fabricRef.current) return;
    const s = CANVAS_SCALE;
    const rect = new fabric.Rect({
      left: 100 * s, top: 200 * s, width: 150 * s, height: 30 * s,
      fill: 'rgba(248,250,252,0.95)', stroke: '#000', strokeWidth: 0.8,
      borderColor: '#3b82f6', cornerColor: '#3b82f6', cornerStyle: 'circle',
      cornerSize: 8, transparentCorners: false,
      _elId: `rect_${Date.now()}`, _elType: 'rect', _label: 'ラベル',
    });
    rect.on('after:render', function(opt) {
      if (!this.canvas) return;
      const ctx = this.canvas.getContext('2d');
      ctx.save();
      ctx.font = `${6 * s}px sans-serif`;
      ctx.fillStyle = '#555';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const c = this.getCenterPoint();
      ctx.fillText(this._label || '', c.x, c.y);
      ctx.restore();
    });
    fabricRef.current.add(rect);
    fabricRef.current.setActiveObject(rect);
    fabricRef.current.renderAll();
  };

  const addLine = () => {
    if (!fabricRef.current) return;
    const s = CANVAS_SCALE;
    const line = new fabric.Line([100 * s, 100 * s, 400 * s, 100 * s], {
      stroke: '#000', strokeWidth: 1,
      borderColor: '#3b82f6', cornerColor: '#3b82f6',
      _elId: `line_${Date.now()}`, _elType: 'line',
    });
    fabricRef.current.add(line);
    fabricRef.current.setActiveObject(line);
    fabricRef.current.renderAll();
  };

  const addTable = () => {
    if (!fabricRef.current) return;
    const s = CANVAS_SCALE;
    const rows = 4, cols = 3, cellW = 80 * s, cellH = 22 * s;
    // テーブル全体を1つのRectとして追加
    const totalW = cellW * cols;
    const totalH = cellH * rows;
    const rect = new fabric.Rect({
      left: 80 * s, top: 200 * s, width: totalW, height: totalH,
      fill: 'rgba(248,250,252,0.95)', stroke: '#000', strokeWidth: 0.8,
      borderColor: '#3b82f6', cornerColor: '#3b82f6', cornerStyle: 'circle',
      cornerSize: 8, transparentCorners: false,
      _elId: `table_${Date.now()}`, _elType: 'rect',
      _label: `${rows}行 x ${cols}列 テーブル`,
    });
    rect.on('after:render', function(opt) {
      if (!this.canvas) return;
      const ctx = this.canvas.getContext('2d');
      ctx.save();
      const l = this.left, t = this.top;
      const w = this.width * (this.scaleX || 1);
      const h = this.height * (this.scaleY || 1);
      const cw = w / cols, ch = h / rows;
      ctx.strokeStyle = '#999';
      ctx.lineWidth = 0.5;
      for (let r = 1; r < rows; r++) {
        ctx.beginPath(); ctx.moveTo(l, t + ch * r); ctx.lineTo(l + w, t + ch * r); ctx.stroke();
      }
      for (let c = 1; c < cols; c++) {
        ctx.beginPath(); ctx.moveTo(l + cw * c, t); ctx.lineTo(l + cw * c, t + h); ctx.stroke();
      }
      ctx.font = `${6 * s}px sans-serif`;
      ctx.fillStyle = '#999';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      for (let c = 0; c < cols; c++) {
        ctx.fillText(`列${c + 1}`, l + cw * c + cw / 2, t + ch / 2);
      }
      ctx.restore();
    });
    fabricRef.current.add(rect);
    fabricRef.current.setActiveObject(rect);
    fabricRef.current.renderAll();
  };

  const deleteSelected = () => {
    if (!fabricRef.current) return;
    const obj = fabricRef.current.getActiveObject();
    if (obj && obj.selectable !== false) {
      fabricRef.current.remove(obj);
      fabricRef.current.renderAll();
      setSelected(null);
    }
  };

  const duplicateSelected = () => {
    if (!fabricRef.current) return;
    const obj = fabricRef.current.getActiveObject();
    if (!obj) return;
    obj.clone().then(cloned => {
      cloned.set({ left: obj.left + 20, top: obj.top + 20, _elId: `clone_${Date.now()}` });
      fabricRef.current.add(cloned);
      fabricRef.current.setActiveObject(cloned);
      fabricRef.current.renderAll();
    });
  };

  const updateSelectedProp = (prop, val) => {
    if (!fabricRef.current) return;
    const obj = fabricRef.current.getActiveObject();
    if (!obj) return;
    const s = CANVAS_SCALE;
    if (prop === 'left') obj.set('left', val * s);
    else if (prop === 'top') obj.set('top', val * s);
    else if (prop === 'fontSize') obj.set('fontSize', val * s);
    else if (prop === 'text') obj.set('text', val);
    else if (prop === 'textAlign') obj.set('textAlign', val);
    else if (prop === 'fontWeight') obj.set('fontWeight', val);
    obj.setCoords();
    fabricRef.current.renderAll();
    updateSelected(obj);
  };

  return (
    <div className="space-y-3" data-testid="pdf-design-page">
      {/* ヘッダー */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold tracking-tight">PDFデザインエディター</h1>
          <p className="text-xs text-muted-foreground">
            {currentTemplateName ? `編集中: ${currentTemplateName}` : 'マウスでドラッグ&リサイズ'}
          </p>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => { setShowSaveAs(true); setSaveAsName(''); }}
            data-testid="save-as-btn">
            <FilePlus size={14} className="mr-1" />名前をつけて保存
          </Button>
          <Button variant="outline" size="sm" onClick={handleSaveTemplate} disabled={saving}
            data-testid="save-btn">
            {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
            {currentTemplateId ? '上書き保存' : '保存'}
          </Button>
          <Button variant="outline" size="sm" onClick={handlePreview} disabled={previewing}>
            {previewing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Eye size={14} className="mr-1" />}プレビュー
          </Button>
          <Button variant="outline" size="sm" onClick={handleReset}><RotateCcw size={14} className="mr-1" />リセット</Button>
          <input ref={fileInputRef} type="file" accept=".json" className="hidden" onChange={handleImport} />
          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}
            data-testid="import-btn">
            <Upload size={14} className="mr-1" />インポート
          </Button>
        </div>
      </div>

      {/* 名前をつけて保存ダイアログ */}
      {showSaveAs && (
        <div className="border border-blue-200 bg-blue-50 rounded-lg p-3 flex items-center gap-2"
          data-testid="save-as-dialog">
          <Label className="text-xs whitespace-nowrap">テンプレート名:</Label>
          <Input className="h-8 text-sm max-w-xs" value={saveAsName}
            onChange={e => setSaveAsName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSaveAs()}
            placeholder="テンプレート名を入力" autoFocus />
          <Button size="sm" onClick={handleSaveAs}><Check size={14} className="mr-1" />保存</Button>
          <Button variant="ghost" size="sm" onClick={() => setShowSaveAs(false)}><X size={14} /></Button>
        </div>
      )}

      {/* タブ */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-sm">
          <TabsTrigger value="invoice">請求書</TabsTrigger>
          <TabsTrigger value="detail">明細書</TabsTrigger>
          <TabsTrigger value="record">実績記録票</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* ツールバー */}
      <div className="flex items-center gap-1 bg-muted p-1.5 rounded-lg flex-wrap" data-testid="toolbar">
        <Button variant="outline" size="sm" onClick={addText} className="h-7 text-xs gap-1">
          <Type size={13} /> テキスト
        </Button>
        <Button variant="outline" size="sm" onClick={addRect} className="h-7 text-xs gap-1">
          <Square size={13} /> 枠
        </Button>
        <Button variant="outline" size="sm" onClick={addTable} className="h-7 text-xs gap-1">
          <Grid3x3 size={13} /> テーブル
        </Button>
        <Button variant="outline" size="sm" onClick={addLine} className="h-7 text-xs gap-1">
          <Minus size={13} /> 線
        </Button>
        <div className="w-px h-5 bg-border mx-1" />
        <Button variant="outline" size="sm" onClick={duplicateSelected} className="h-7 text-xs gap-1" disabled={!selected}>
          <Copy size={13} /> 複製
        </Button>
        <Button variant="destructive" size="sm" onClick={deleteSelected} className="h-7 text-xs gap-1" disabled={!selected}>
          <Trash2 size={13} /> 削除
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-3">
        {/* キャンバス */}
        <div className="xl:col-span-6">
          <Card>
            <CardContent className="p-2 flex justify-center overflow-auto bg-gray-100">
              <div className="shadow-lg">
                <canvas ref={canvasRef} data-testid="design-canvas" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* プロパティパネル */}
        <div className="xl:col-span-3">
          <Card>
            <CardHeader className="py-2 px-3">
              <CardTitle className="text-xs">
                {selected ? `選択: ${selected.id}` : '要素を選択してください'}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 space-y-3">
              {selected ? (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-[10px] text-muted-foreground">X (pt)</Label>
                      <Input type="number" className="h-7 text-xs" value={selected.left}
                        onChange={e => updateSelectedProp('left', +e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-muted-foreground">Y (pt)</Label>
                      <Input type="number" className="h-7 text-xs" value={selected.top}
                        onChange={e => updateSelectedProp('top', +e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-muted-foreground">幅</Label>
                      <Input type="number" className="h-7 text-xs" value={selected.width} readOnly />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-muted-foreground">高さ</Label>
                      <Input type="number" className="h-7 text-xs" value={selected.height} readOnly />
                    </div>
                  </div>
                  {(selected.type === 'textbox') && (
                    <>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">テキスト</Label>
                        <Input className="h-7 text-xs" value={selected.text}
                          onChange={e => updateSelectedProp('text', e.target.value)} />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-[10px] text-muted-foreground">サイズ (pt)</Label>
                          <Input type="number" className="h-7 text-xs" value={selected.fontSize || 12}
                            onChange={e => updateSelectedProp('fontSize', +e.target.value)} />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] text-muted-foreground">太さ</Label>
                          <select className="h-7 text-xs border rounded px-2 w-full"
                            value={selected.fontWeight || 'normal'}
                            onChange={e => updateSelectedProp('fontWeight', e.target.value)}>
                            <option value="normal">通常</option>
                            <option value="bold">太字</option>
                          </select>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">配置</Label>
                        <div className="flex gap-1">
                          {['left', 'center', 'right'].map(a => (
                            <Button key={a} variant={selected.textAlign === a ? 'default' : 'outline'}
                              size="sm" className="h-7 flex-1 text-xs"
                              onClick={() => updateSelectedProp('textAlign', a)}>
                              {a === 'left' ? <AlignLeft size={12} /> : a === 'center' ? <AlignCenter size={12} /> : <AlignRight size={12} />}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                  {(selected.type === 'rect') && (
                    <div className="space-y-1">
                      <Label className="text-[10px] text-muted-foreground">ラベル</Label>
                      <Input className="h-7 text-xs" value={selected.label}
                        onChange={e => {
                          const obj = fabricRef.current?.getActiveObject();
                          if (obj) { obj._label = e.target.value; fabricRef.current.renderAll(); }
                          setSelected(prev => ({ ...prev, label: e.target.value }));
                        }} />
                    </div>
                  )}
                </>
              ) : (
                <p className="text-xs text-muted-foreground py-8 text-center">
                  キャンバス上の要素をクリックして選択
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* PDFプレビュー */}
        <div className="xl:col-span-3">
          <Card className="sticky top-4">
            <CardHeader className="py-2 px-3">
              <CardTitle className="text-xs flex items-center gap-1">
                <FileText size={12} /> PDFプレビュー
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {previewUrl ? (
                <iframe src={previewUrl} className="w-full border-0" style={{ height: '35vh' }} title="PDF Preview" />
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                  <Eye size={24} className="mb-2 opacity-30" />
                  <p className="text-[10px]">プレビューで確認</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* テンプレートファイル管理 */}
          <Card className="mt-3">
            <CardHeader className="py-2 px-3">
              <CardTitle className="text-xs flex items-center gap-1">
                <FolderOpen size={12} /> 保存済みテンプレート ({templates.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2 space-y-1.5 max-h-[30vh] overflow-y-auto">
              {templates.length === 0 ? (
                <p className="text-[10px] text-muted-foreground text-center py-4">
                  テンプレートがありません
                </p>
              ) : templates.map(t => (
                <div key={t.id}
                  className={`border rounded-md p-2 text-xs transition-colors cursor-pointer
                    ${currentTemplateId === t.id ? 'border-blue-400 bg-blue-50' : 'hover:bg-gray-50'}`}
                  data-testid={`template-${t.id}`}>
                  <div className="flex items-center justify-between gap-1">
                    {renamingId === t.id ? (
                      <div className="flex items-center gap-1 flex-1">
                        <Input className="h-6 text-xs flex-1" value={renameValue}
                          onChange={e => setRenameValue(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleRename(t.id)} autoFocus />
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleRename(t.id)}>
                          <Check size={12} />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setRenamingId(null)}>
                          <X size={12} />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-1.5 flex-1 min-w-0"
                          onClick={() => handleLoadTemplate(t.id)}>
                          {t.is_default && <Star size={11} className="text-amber-500 flex-shrink-0" fill="currentColor" />}
                          <span className="truncate font-medium">{t.name}</span>
                        </div>
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          <Button variant="ghost" size="sm" className="h-5 w-5 p-0"
                            title="リネーム"
                            onClick={(e) => { e.stopPropagation(); setRenamingId(t.id); setRenameValue(t.name); }}>
                            <Pencil size={10} />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-5 w-5 p-0"
                            title="デフォルトに設定"
                            onClick={(e) => { e.stopPropagation(); handleSetDefault(t.id); }}>
                            <Star size={10} className={t.is_default ? 'text-amber-500' : ''} />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-5 w-5 p-0"
                            title="エクスポート"
                            onClick={(e) => { e.stopPropagation(); handleExport(t.id); }}>
                            <Download size={10} />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-red-500"
                            title="削除"
                            onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(t.id); }}>
                            <Trash2 size={10} />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="text-[9px] text-muted-foreground mt-0.5">
                    {t.doc_type !== 'all' ? t.doc_type : '全タイプ'} ・ {t.updated_at?.slice(0, 10)}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
